import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, ChevronDownIcon, SparklesIcon, MessageSquareIcon, TrendingDownIcon, CheckCheckIcon, UserIcon, BriefcaseIcon, SettingsIcon, LogOutIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { RegionSelector } from './RegionSelector';
import { CartButton } from './cart/CartButton';
import { AuthButton } from './AuthButton';
type Notification = {
  id: string;
  icon: React.ElementType;
  title: string;
  detail: string;
  time: string;
  read: boolean;
};
const initialNotifications: Notification[] = [{
  id: '1',
  icon: SparklesIcon,
  title: '3 new matches found',
  detail: 'For "Prado under AED 90,000"',
  time: '5m ago',
  read: false
}, {
  id: '2',
  icon: MessageSquareIcon,
  title: 'Seller replied',
  detail: 'Al Quoz Motors sent you a message',
  time: '1h ago',
  read: false
}, {
  id: '3',
  icon: TrendingDownIcon,
  title: 'Price dropped',
  detail: 'A saved villa is now AED 120k lower',
  time: 'Yesterday',
  read: true
}];
export function TopHeader() {
  const {
    isAuthenticated,
    openLogin,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>(initialNotifications);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const unread = notifications.filter(n => !n.read).length;
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);
  const go = (to: string) => {
    setMenuOpen(false);
    navigate(to);
  };
  const markAllRead = () => setNotifications(prev => prev.map(n => ({
    ...n,
    read: true
  })));
  return <header className="hidden lg:flex items-center justify-end gap-4 px-8 py-5 absolute top-0 right-0 z-50 pointer-events-none [&>*]:pointer-events-auto" data-id="element-4795" data-mp="2albj15oh73ru">
      <CartButton data-id="element-4796" data-mp="2kc63c4i5vtan" />
      <RegionSelector data-id="element-4797" data-mp="2kbmm6q0c58xo" />
      {isAuthenticated ? <div className="flex items-center gap-4" data-id="element-4798" data-mp="2kcxxdaw41iyt">
          <div className="relative" ref={panelRef} data-id="element-4799" data-mp="2yb5sdd9l6a7c">
            <button onClick={() => setOpen(o => !o)} aria-label="Notifications" className={`relative p-2 rounded-full transition-colors ${open ? 'text-primary-indigo bg-primary-indigo/5' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`} data-id="element-4800" data-mp="2y9uqjizpbciv">
              <BellIcon className="w-5 h-5" data-id="element-4801" data-mp="3hvd2d9xi7anu" />
              {unread > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-error text-white text-[10px] font-bold flex items-center justify-center rounded-full" data-id="element-4802" data-mp="33xoh40ziy1i9">
                  {unread}
                </span>}
            </button>

            <AnimatePresence data-id="element-4803" data-mp="2yzd8ftkytx1m">
              {open && <motion.div initial={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} transition={{
            duration: 0.15
          }} className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-[0_12px_40px_rgb(0,0,0,0.12)] border border-slate-100 overflow-hidden z-50" data-id="element-4804" data-mp="2z0y27gzghbyf">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100" data-id="element-4805" data-mp="1ug999s0tutvw">
                    <h3 className="font-display font-bold text-dark-navy" data-id="element-4806" data-mp="1ph6c0tr39ryh">
                      Notifications
                    </h3>
                    {unread > 0 && <button onClick={markAllRead} className="flex items-center gap-1 text-xs font-medium text-primary-indigo hover:underline" data-id="element-4807" data-mp="1kk6a8v19vbd6">
                        <CheckCheckIcon className="w-3.5 h-3.5" data-id="element-4808" data-mp="39x1bb3ob1kwv" />
                        Mark all read
                      </button>}
                  </div>

                  <div className="max-h-80 overflow-y-auto" data-id="element-4809" data-mp="35tbp2kv3rff0">
                    {notifications.map(n => {
                const Icon = n.icon;
                return <div key={n.id} className={`flex gap-3 px-4 py-3 border-b border-slate-50 last:border-0 transition-colors hover:bg-slate-50 ${!n.read ? 'bg-primary-indigo/[0.03]' : ''}`} data-id="element-4810" data-mp="wkx72h1nv9s8">
                          <div className="w-9 h-9 rounded-full bg-primary-indigo/10 flex items-center justify-center flex-shrink-0" data-id="element-4811" data-mp="8pugo4n7ky9n">
                            <Icon className="w-4 h-4 text-primary-indigo" data-id="element-4812" data-mp="dq01p1yz3mir" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-4813" data-mp="hrlj90mgcoza">
                            <p className="text-sm font-medium text-dark-navy" data-id="element-4814" data-mp="mpn7dzyqjehm">
                              {n.title}
                            </p>
                            <p className="text-xs text-slate-500 truncate" data-id="element-4815" data-mp="1y3qwosudoegc">
                              {n.detail}
                            </p>
                            <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-4816" data-mp="3u5kkc8o1lmz9">
                              {n.time}
                            </p>
                          </div>
                          {!n.read && <span className="w-2 h-2 bg-primary-indigo rounded-full flex-shrink-0 mt-1.5" data-id="element-4817" data-mp="7yw16z9t2qn2" />}
                        </div>;
              })}
                  </div>

                  <button className="w-full py-3 text-sm font-medium text-primary-indigo hover:bg-slate-50 transition-colors border-t border-slate-100" data-id="element-4818" data-mp="36hb5xrt5s1i5">
                    View all
                  </button>
                </motion.div>}
            </AnimatePresence>
          </div>

          <div className="relative" ref={menuRef} data-id="element-4819" data-mp="2spwhv6ke9a48">
            <button onClick={() => setMenuOpen(o => !o)} aria-label="Account menu" aria-haspopup="menu" aria-expanded={menuOpen} className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" data-id="element-4820" data-mp="2soldb4j4jyc2">
              <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="User avatar" className="w-10 h-10 rounded-full object-cover border border-slate-200" data-id="element-4821" data-mp="18128sux98h1h" />
              <ChevronDownIcon className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`} data-id="element-4822" data-mp="1m0jzxgy0w6e0" />
            </button>

            <AnimatePresence data-id="element-4823" data-mp="1cxiwjo0jkte1">
              {menuOpen && <motion.div initial={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} transition={{
            duration: 0.15
          }} role="menu" className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-[0_12px_40px_rgb(0,0,0,0.12)] border border-slate-100 overflow-hidden z-50" data-id="element-4824" data-mp="1hxnd1e1g9o49">
                  <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100" data-id="element-4825" data-mp="1gv9fyhfmdzkw">
                    <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200" data-id="element-4826" data-mp="1bysslpg8vp8v" />
                    <div className="min-w-0" data-id="element-4827" data-mp="1zon525wnjbsl">
                      <p className="text-sm font-bold text-dark-navy truncate" data-id="element-4828" data-mp="1uwlwatyp037b">
                        Ahmed Al Mansoori
                      </p>
                      <p className="text-xs text-slate-500 truncate" data-id="element-4829" data-mp="228s2j0qumynm">
                        ahmed@example.com
                      </p>
                    </div>
                  </div>

                  <div className="py-1" data-id="element-4830" data-mp="2c4mr0chr7nyb">
                    <button role="menuitem" onClick={() => go('/profile')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-4831" data-mp="2c80whtlpsd9z">
                      <UserIcon className="w-4 h-4 text-slate-400" data-id="element-4832" data-mp="2q3urr9tkf44m" />
                      View Profile
                    </button>
                    <button role="menuitem" onClick={() => go('/business')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-4833" data-mp="2zv90m87epgaf">
                      <BriefcaseIcon className="w-4 h-4 text-slate-400" data-id="element-4834" data-mp="38tugtwpeoj81" />
                      Business Hub
                    </button>
                    <button role="menuitem" onClick={() => go('/business')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-4835" data-mp="29wlg47ps82r5">
                      <SettingsIcon className="w-4 h-4 text-slate-400" data-id="element-4836" data-mp="2om14jh1ubw3o" />
                      Settings
                    </button>
                  </div>

                  <div className="py-1 border-t border-slate-100" data-id="element-4837" data-mp="2smb7ekuc66eb">
                    <button role="menuitem" onClick={() => {
                setMenuOpen(false);
                logout();
                navigate('/');
              }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:bg-error/5 transition-colors" data-id="element-4838" data-mp="2xm7ggyhw4o0q">
                      <LogOutIcon className="w-4 h-4" data-id="element-4839" data-mp="10oryqmtp6pw4" />
                      Log out
                    </button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div> : <AuthButton onClick={openLogin} data-id="element-4840" data-mp="qyq7jm61srcz" />}
    </header>;
}